import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react"

import type { EntryWithVote, Votes } from "../../../api/hooks/vote"
import { useUnsavedWarning } from "./unsaved-context"

interface VotingContextCtxProps {
	currentVotes: number
	isEnoughVotes: boolean
	isChanged: boolean
	voted: Votes
	unvoted: Votes
	toggleVote: (entry: EntryWithVote, actualSelection: boolean) => void
	resetChanges: () => void
	totalEntries: number
}

interface ChallengeProviderProps {
	previouslySubmittedVotes: number
	totalEntries: number
	children: ReactNode
}

const VotingContext = createContext({} as VotingContextCtxProps)

const VoteContextProvider = ({
	previouslySubmittedVotes,
	totalEntries,
	children,
}: ChallengeProviderProps) => {
	const setDirty = useUnsavedWarning()

	const [voted, setVoted] = useState<Votes>(new Set())
	const [unvoted, setUnvoted] = useState<Votes>(new Set())
	const [updatedVotes, setUpdatedVotes] = useState(0)

	const withAddedVotes = voted.size > 0
	const withRemovedVotes = unvoted.size > 0
	const isChanged = withAddedVotes || withRemovedVotes

	const currentVotes = updatedVotes + previouslySubmittedVotes
	const isEnoughVotes = currentVotes >= 3

	useEffect(() => {
		setDirty(isChanged)
	}, [isChanged, setDirty])

	const toggleVote = useCallback(
		(entry: EntryWithVote, isCurrentlySelected: boolean) => {
			const { id: entryId, isSelected } = entry

			if (!isCurrentlySelected) {
				setUnvoted(unvoted => {
					const newUnvoted = new Set(unvoted)
					newUnvoted.delete(entryId)

					return newUnvoted
				})
				setVoted(voted => {
					const newVoted = new Set(voted)
					newVoted.add(entryId)

					return newVoted
				})
				setUpdatedVotes(votes => votes + 1)
			} else {
				setVoted(voted => {
					const newVoted = new Set(voted)
					newVoted.delete(entryId)

					return newVoted
				})
				if (isSelected) {
					setUnvoted(unvoted => {
						const newUnvoted = new Set(unvoted)
						newUnvoted.add(entryId)

						return newUnvoted
					})
				}
				setUpdatedVotes(votes => votes - 1)
			}
		},
		[],
	)

	const resetChanges = () => {
		setVoted(new Set())
		setUnvoted(new Set())
	}

	return (
		<VotingContext.Provider
			value={{
				voted,
				unvoted,
				totalEntries: totalEntries || 0,
				currentVotes,
				isEnoughVotes: isEnoughVotes,
				isChanged,
				toggleVote,
				resetChanges,
			}}
		>
			{children}
		</VotingContext.Provider>
	)
}

const useVoting = () => useContext(VotingContext)

export { VoteContextProvider, useVoting }
